import {HttpStatusCode} from 'axios';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {InformationModal, PhoneInput, Card, Button, CopyText, Input, TestAttribute, notifier} from 'components';
import {UserContext} from 'context';
import {AgentParticipant, Session} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {logger, sessionUtils, isEmailValid, isPhoneNumberValid} from 'utils';

type InviteCardProps = {
    title: string;
    children: React.ReactNode;
}

const InviteCard: React.FC<InviteCardProps> = ({title, children}: InviteCardProps) => (
    <Card className='tw-h-40'>
        <div className='tw-ts-base-bold'>{title}</div>
        <div>{children}</div>
    </Card>
);

type Props = {
    open: boolean;
    session: Session<AgentParticipant>;
    participantId?: string;
    onParticipantInvite: (participantId: string, type: string, contact: string) => Promise<any>;
    onClose: () => void;
};

const T_KEYS_IN_SESSION = TRANSLATION_KEYS.IN_SESSION_PAGE;
const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.INVITE_MODAL;

export const InviteModal: React.FC<Props> = ({open, session, participantId, onParticipantInvite, onClose}: Props) => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);
    const [email, setEmail] = useState('');
    const [inviteInProgress, setInviteInProgress] = useState({sms: false, email: false});
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isPhoneValid, setIsPhoneValid] = useState(false);

    const participant = useMemo(() => sessionUtils.getParticipantById(session, participantId), [session, participantId]);
    const {code, pin} = useMemo(() => {
        if (participant?.data?.inviteParams) {
            return participant.data.inviteParams;
        }
        return session.inviteClientParams;
    }, [participant?.data?.inviteParams, session.inviteClientParams]);
    const enterOnLink = useMemo(() => window.location.origin + `/${participant?.role || 'client'}/join`, [participant?.role]);

    const link = useMemo(() => {
        if (participant) {
            return sessionUtils.getParticipantJoinSessionLink(session, participant);
        } else if (session.inviteClientParams) {
            return session.inviteClientParams.link;
        }
    }, [participant, session]);

    const handlePhoneNumberChange = useCallback((value: string, data: {format: string}) => {
        setPhoneNumber(value);
        setIsPhoneValid(isPhoneNumberValid(value, data));
    }, []);

    const invite = useCallback(async (type: string, contact: string) => {
        try {
            setInviteInProgress((prevValue) => ({...prevValue, [type]: true}));
            await onParticipantInvite(participantId, type, contact);
            notifier.success({title: t(T_KEYS_IN_SESSION.MESSAGES.SEND_INVITE_SUCCESS)});
        } catch (error: any) {
            notifier.error({
                title: t(T_KEYS_IN_SESSION.MESSAGES.SEND_INVITE_FAILED),
                ...(error?.response?.status === HttpStatusCode.TooManyRequests && {
                    details: (t(T_KEYS_IN_SESSION.MESSAGES.SEND_INVITE_FAILED_TOO_MANY_REQUESTS))
                })
            });
            logger.error(error);
        } finally {
            setInviteInProgress((prevValue) => ({...prevValue, [type]: false}));
        }
    }, [t, participantId, onParticipantInvite]);

    const inviteBySms = useCallback(() => invite('sms', phoneNumber), [phoneNumber, invite]);

    const inviteByEmail = useCallback(() => invite('email', email), [email, invite]);

    useEffect(() => {
        if (!open) {
            setEmail('');
            setPhoneNumber('');
        }
    }, [open]);

    return (
        <InformationModal
            open={open}
            title={t(T_KEYS.TITLE)}
            maxWidth='sm'
            fullWidth
            onClose={onClose}
        >
            <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4'>
                <InviteCard title={t(T_KEYS.INVITE_VIA_LINK)}>
                    <div className='tw-my-4'>
                        <TestAttribute testAttribute={{id: 'invite-via-link'}}>
                            <span className='tw-break-all'>{link}</span>
                        </TestAttribute>
                    </div>
                    <div className='tw-text-right'>
                        <CopyText text={link} copiedLabelOnly>
                            <Button testAttribute={{id: 'copy-link-to-clipboard-button'}}>{t(T_KEYS.COPY_TO_CLIPBOARD)}</Button>
                        </CopyText>
                    </div>
                </InviteCard>
                <InviteCard title={t(T_KEYS.INVITE_VIA_CODE)}>
                    <div className='tw-mt-4'>
                        <div className='tw-text-sm tw-mb-5'>
                            <div className='tw-text-default-dimmed tw-mb-1'>{t(T_KEYS.CLIENT_TO_ENTER)}</div>
                            <TestAttribute testAttribute={{id: 'invite-via-code-link'}}>
                                <div className='tw-text-break-word'>{enterOnLink}</div>
                            </TestAttribute>
                        </div>
                        <div className='tw-flex tw-items-center'>
                            <TestAttribute testAttribute={{id: 'code-for-invite-via-code'}}>
                                <div className='tw-mr-8 tw-ts-base-bold'>
                                    <span className='tw-text-default-dimmed'>{t(T_KEYS.CODE)}</span>
                                    {' '}
                                    {code}
                                </div>
                            </TestAttribute>
                            <TestAttribute testAttribute={{id: 'pin-for-invite-via-code'}}>
                                <div className='tw-ts-base-bold'>
                                    <span className='tw-text-default-dimmed'>{t(T_KEYS.PIN)}</span>
                                    {' '}
                                    {pin}
                                </div>
                            </TestAttribute>
                        </div>
                    </div>
                </InviteCard>
                {settings.clientInvitation?.sms?.enabled &&
                    <InviteCard title={t(T_KEYS.INVITE_VIA_SMS)}>
                        <div className='tw-my-4'>
                            <PhoneInput
                                label={t(T_KEYS.PHONE_PLACEHOLDER)}
                                value={phoneNumber}
                                disabled={inviteInProgress.sms}
                                underlined
                                onChange={handlePhoneNumberChange}
                            />
                        </div>
                        <div className='tw-text-right'>
                            <Button
                                disabled={!isPhoneValid}
                                loading={inviteInProgress.sms}
                                onClick={inviteBySms}
                            >
                                {t(T_KEYS.SEND_SMS_BTN)}
                            </Button>
                        </div>
                    </InviteCard>}
                {settings.clientInvitation?.email?.enabled &&
                    <InviteCard title={t(T_KEYS.INVITE_VIA_EMAIL)}>
                        <div className='tw-my-4'>
                            <Input
                                type='email'
                                value={email}
                                label={t(T_KEYS.EMAIL_PLACEHOLDER)}
                                disabled={inviteInProgress.email}
                                onChange={setEmail}
                            />
                        </div>
                        <div className='tw-text-right'>
                            <Button
                                disabled={!isEmailValid(email)}
                                loading={inviteInProgress.email}
                                onClick={inviteByEmail}
                            >
                                {t(T_KEYS.SEND_EMAIL_BTN)}
                            </Button>
                        </div>
                    </InviteCard>}
            </div>
        </InformationModal>
    );
};
